
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {
  fetchCostsViewTraffic,
  changeOrdersSort,
  fetchCostsViewOrders,
  changeSelectedCostMetric,
  changeSort
} from 'redux/modules/insights/actions'
import {setTimePeriod} from 'redux/modules/currentUser/actions'
import {
  getPaidOrders,
  getPaidTrafficData,
  getSelectedMetric,
  getMetrics,
  getSorting
} from 'redux/modules/insights/selectors'
import {
  getCurrentTimePeriod,
  getTimePeriodRanges,
  getAggregatedSites
} from 'redux/modules/currentUser/selectors'
import isRangeEqual from 'utils/isRangeEqual'
import OverviewTable from 'components/OverviewTable'
import ColorLegend from 'components/ColorLegend'
import formatTimePeriodDate from 'utils/formatTimePeriodDate'
import isEqual from 'lodash/isEqual'
import {getSelectedDateRange} from 'redux/modules/currentUser/selectors'

const METRICS = {
  'cost_per_visit': {
    metric: ['cost:perVisit'],
    label: 'Cost / Visit',
    dataKey: 'costPerVisit'
  },
  'cost_per_order': {
    metric: 'cost:perOrder',
    label: 'Cost / Conversion',
    dataKey: 'costPerOrder'
  }
}

export class CostsView extends React.Component {
  static propTypes = {
    today: PropTypes.instanceOf(Date),
    data: PropTypes.array,
    sortKey: PropTypes.string,
    sortDirection: PropTypes.string,
    fetchCostsViewOrders: PropTypes.func,
    fetchCostsViewTraffic: PropTypes.func,
    changeSort: PropTypes.func,
    changeOrdersSort: PropTypes.func,
    timePeriod: PropTypes.string,
    dynamicDateType: PropTypes.string,
    timePeriodRanges: PropTypes.object,
    metrics: PropTypes.object,
    isFetching: PropTypes.bool,
    sites: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    setTimePeriod: PropTypes.func,
    metric: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    label: PropTypes.string,
    dataKey: PropTypes.string,
    selectedCostMetric: PropTypes.string,
    changeSelectedCostMetric: PropTypes.func,
    isOrders: PropTypes.bool,
    crossSiteMode: PropTypes.bool
  }

  static contextTypes = {
    currency: PropTypes.string,
    timezone: PropTypes.string
  }

  componentDidMount() {
    const {currency, timezone} = this.context
    const {
      fetchCostsViewOrders, fetchCostsViewTraffic, metric, selectedCostMetric, isOrders, crossSiteMode, today, setTimePeriod
    } = this.props
    isOrders
      ? fetchCostsViewOrders(selectedCostMetric, crossSiteMode, currency, timezone)
      : fetchCostsViewTraffic(metric, crossSiteMode, currency, timezone)
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const {currency, timezone} = nextContext
    const {
      fetchCostsViewOrders,
      fetchCostsViewTraffic,
      sites,
      timePeriod,
      timePeriodRanges,
      metric,
      selectedCostMetric,
      crossSiteMode
    } = this.props
    const {timePeriodRanges: nextTimePeriodRanges, isOrders} = nextProps
    const sitesChanged = !isEqual(sites, nextProps.sites)

    if (!nextTimePeriodRanges || !timePeriodRanges) return null

    const timePeriodChanged = timePeriod !== nextProps.timePeriod
    const timeRangesValid = !!nextTimePeriodRanges.start && !!nextTimePeriodRanges.end
    if (!timeRangesValid) return null

    const timeRangesChanged = !(
      isRangeEqual(timePeriodRanges.start, nextTimePeriodRanges.start) &&
      isRangeEqual(timePeriodRanges.end, nextTimePeriodRanges.end)
    )
    const metricChanged = (metric !== nextProps.metric) || (selectedCostMetric !== nextProps.selectedCostMetric)
    if (metricChanged || sitesChanged || timePeriodChanged || timeRangesChanged) {
      isOrders
        ? fetchCostsViewOrders(nextProps.selectedCostMetric, crossSiteMode, currency, timezone)
        : fetchCostsViewTraffic(nextProps.metric, crossSiteMode, currency, timezone)
    }
  }

  render() {
    const {
      timePeriod,
      dynamicDateType,
      timePeriodRanges,
      data,
      sortKey,
      today,
      sortDirection,
      isFetching,
      setTimePeriod,
      changeSort,
      changeOrdersSort,
      isOrders,
      label,
      dataKey,
      metrics,
      selectedCostMetric
    } = this.props

    if (!timePeriodRanges) return null

    const currentPeriodName = formatTimePeriodDate(timePeriod, timePeriodRanges.start.start)
    const anotherPeriodName = formatTimePeriodDate(timePeriod, timePeriodRanges.end.start)
    const columns = [
      {
        type: 'chart',
        label: (
          <div className='table-cell is-grow'>
            {label}
            <ColorLegend
              title={`These graphs show comparison between ${currentPeriodName} and ${anotherPeriodName}.`}
              inverted
              visible
            />
          </div>
        ),
        width: 'grow',
        dataKey,
        accumulatedValues: false,
        format: 'currency'
      },
      {
        type: 'value',
        headerKey: `${dataKey}Sum`,
        label,
        description: `avg ${currentPeriodName}`,
        width: '100px',
        dataKey: `${dataKey}Sum`,
        format: 'currency',
        applyColor: false
      },
      {
        type: 'value',
        headerKey: `${dataKey}AbsChange`,
        label: 'Change',
        description: `comp ${anotherPeriodName}`,
        width: '100px',
        dataKey: `${dataKey}AbsoluteSumChange`,
        format: 'signed-number',
        applyColor: true
      },
      {
        type: 'value',
        headerKey: `${dataKey}RelChange`,
        label: '% change',
        description: `comp ${anotherPeriodName}`,
        width: '100px',
        dataKey: `${dataKey}RelativeSumChange`,
        format: 'signed-percent',
        applyColor: true
      }
    ]

    return (
      <div className='costs-view'>
        <OverviewTable
          className='costs-view_table'
          timePeriod={timePeriod}
          timePeriodRanges={timePeriodRanges}
          data={data}
          isLoading={isFetching}
          disabled={isFetching}
          sortKey={sortKey}
          sortDirection={sortDirection}
          today={today}
          setTimePeriod={setTimePeriod}
          dynamicDateType={dynamicDateType}
          changeSort={isOrders ? changeOrdersSort : changeSort}
          columns={columns}
          selectedMetric={isOrders ? selectedCostMetric : null}
          onMetricChange={isOrders ? this.onMetricChange : null}
          metrics={isOrders ? metrics : null}
          invertAreaColors
          isPaid
        />
      </div>
    )
  }

  onMetricChange = item => {
    const {changeSelectedCostMetric} = this.props
    changeSelectedCostMetric(item.value)
  }
}

const mapStateToProps = (state, props) => {
  const {metric: paramMetric} = props.route
  const isOrders = paramMetric === 'cost_per_order'
  const {
    isFetching,
    selectedCostMetric
  } = isOrders ? state.insights.orders : state.insights.traffic
  const timePeriod = getCurrentTimePeriod(state)
  const timePeriodRanges = getTimePeriodRanges(state)
  const getData = isOrders ? getPaidOrders : getPaidTrafficData
  const {metric, label} = METRICS[paramMetric]
  const metrics = getMetrics('cost_per_order')
  const calculatedCostMetric = getSelectedMetric(selectedCostMetric, Object.keys(metrics))
  const dataKey = isOrders ? calculatedCostMetric.replace(':p', 'P') : METRICS[paramMetric].dataKey
  const {sortKey, sortDirection} = getSorting(state, isOrders)
  const crossSiteMode = props.route.crossSiteMode
  const {dynamicDateType} = getSelectedDateRange(state)

  return {
    today: state.today,
    metric,
    label,
    dataKey,
    timePeriod,
    timePeriodRanges,
    dynamicDateType,
    sites: getAggregatedSites(state, crossSiteMode),
    data: getData(state, timePeriodRanges, dataKey),
    isFetching,
    selectedCostMetric: calculatedCostMetric,
    metrics,
    sortDirection,
    sortKey,
    isOrders,
    crossSiteMode
  }
}

const mapDispatchToProps = {
  fetchCostsViewOrders,
  fetchCostsViewTraffic,
  changeOrdersSort,
  changeSort,
  changeSelectedCostMetric,
  setTimePeriod
}

export default connect(mapStateToProps, mapDispatchToProps)(CostsView)



// WEBPACK FOOTER //
// ./client/views/CostsView/index.js